import React, { useMemo, useState } from "react";
import {
    Box, Grid, Paper, Typography, Tooltip, IconButton,
    CircularProgress, TablePagination, Chip, TextField, MenuItem,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import StarIcon from "@mui/icons-material/Star";
import CategoryIcon from "@mui/icons-material/Category";

import { SECTION_COLORS, ROWS_PER_PAGE } from "../../constants";
import EmptyState from "../shared/EmptyState";
import ErrorState from "../shared/ErrorState";
import StatusChip from "../shared/StatusChip";
import ListToolbar from "../shared/ListToolbar";

const ProductsListSection = ({
    productsApi,
    productCategories = [],
    page,
    onPageChange,
    onView,
    onEdit,
    onDelete,
}) => {
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [sortBy, setSortBy] = useState("name_asc");

    const sourceData = Array.isArray(productsApi.data) ? productsApi.data : [];

    const categoryMap = useMemo(
        () => Object.fromEntries(productCategories.map((c) => [c.id, c.name])),
        [productCategories],
    );

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const rows = sourceData.filter((p) => {
            if (activeFilter === "active" && !p.active) return false;
            if (activeFilter === "inactive" && p.active) return false;
            if (categoryFilter !== "all") {
                const cid = p.category_id ? String(p.category_id) : "";
                if (cid !== categoryFilter) return false;
            }
            if (!q) return true;
            return String(p.name || "").toLowerCase().includes(q)
                || String(p.match_key || "").toLowerCase().includes(q)
                || String(p.id || "").toLowerCase().includes(q);
        });
        rows.sort((a, b) => {
            if (sortBy === "points_desc") return Number(b.points_value || 0) - Number(a.points_value || 0);
            if (sortBy === "created_desc") return new Date(b.created_at || 0) - new Date(a.created_at || 0);
            if (sortBy === "updated_desc") return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
            return String(a.name || "").localeCompare(String(b.name || ""), "fr");
        });
        return rows;
    }, [sourceData, search, activeFilter, categoryFilter, sortBy]);

    if (productsApi.error) {
        return <ErrorState error={productsApi.error} onRetry={() => productsApi.refetch()} />;
    }
    if (productsApi.loading) {
        return <Box py={4} display="flex" justifyContent="center"><CircularProgress size={28} /></Box>;
    }
    if (sourceData.length === 0) {
        return <EmptyState message="Aucun produit configuré pour cette marque" color={SECTION_COLORS.rewards.main} />;
    }

    const maxPage = Math.max(0, Math.ceil(filtered.length / ROWS_PER_PAGE) - 1);
    const safePage = Math.min(page || 0, maxPage);

    const paginated = filtered.slice(
        safePage * ROWS_PER_PAGE, safePage * ROWS_PER_PAGE + ROWS_PER_PAGE
    );

    return (
        <Box>
            <ListToolbar
                search={search}
                onSearch={setSearch}
                activeFilter={activeFilter}
                onActiveFilter={setActiveFilter}
                searchPlaceholder="Rechercher un produit (nom, match_key, id)…"
                sortValue={sortBy}
                onSortChange={setSortBy}
                sortOptions={[
                    { value: "name_asc", label: "Nom (A-Z)" },
                    { value: "points_desc", label: "Points (décroissant)" },
                    { value: "created_desc", label: "Création (récent)" },
                    { value: "updated_desc", label: "Mise à jour (récent)" },
                ]}
            >
                <TextField
                    select
                    size="small"
                    label="Catégorie"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    sx={{ minWidth: 200, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                >
                    <MenuItem value="all">Toutes</MenuItem>
                    <MenuItem value="">Sans catégorie</MenuItem>
                    {productCategories.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                    ))}
                </TextField>
            </ListToolbar>
            <Grid container spacing={2}>
                {paginated.map((product) => {
                    const categoryName = product.category_id ? categoryMap[product.category_id] : null;
                    return (
                        <Grid item xs={12} sm={6} md={4} key={product.id}>
                            <Paper elevation={0} sx={{
                                p: 2.5, borderRadius: 3, position: "relative", overflow: "hidden",
                                border: `1px solid ${SECTION_COLORS.rewards.main}18`,
                                background: `linear-gradient(135deg, ${SECTION_COLORS.rewards.main}06 0%, transparent 100%)`,
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.10), 0 2px 8px rgba(15, 23, 42, 0.06)",
                                transition: "all 0.25s ease",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14), 0 6px 14px rgba(15, 23, 42, 0.10)",
                                    borderColor: `${SECTION_COLORS.rewards.main}30`,
                                },
                            }}>
                                {/* Name row */}
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <InventoryIcon sx={{ color: SECTION_COLORS.rewards.main, fontSize: 20 }} />
                                    <Typography fontWeight={700} fontSize={14} fontFamily="Montserrat, sans-serif"
                                        color="#1a1a2e" noWrap flex={1}>
                                        {product.name}
                                    </Typography>
                                </Box>

                                {/* Points + Category */}
                                <Box display="flex" flexWrap="wrap" gap={0.5} mb={1.5}>
                                    {product.points_value != null ? (
                                        <Chip
                                            icon={<StarIcon sx={{ fontSize: 13, color: "#D97706 !important" }} />}
                                            label={`${product.points_value} pts`}
                                            size="small"
                                            sx={{ fontSize: 11, bgcolor: "#FEF3C7", color: "#D97706", fontWeight: 700 }}
                                        />
                                    ) : (
                                        <Chip label="Pts: N/A" size="small"
                                            sx={{ fontSize: 10, bgcolor: "#f1f5f9", color: "#94a3b8" }} />
                                    )}
                                    {categoryName && (
                                        <Chip
                                            icon={<CategoryIcon sx={{ fontSize: 12, color: `${SECTION_COLORS.rewards.main} !important` }} />}
                                            label={categoryName}
                                            size="small"
                                            sx={{ fontSize: 10, bgcolor: "#ecf9fd", color: SECTION_COLORS.rewards.main, fontWeight: 600 }}
                                        />
                                    )}
                                </Box>

                                {/* Status + Actions */}
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <StatusChip active={product.active} />
                                    <Box display="flex" gap={0.5}>
                                        <Tooltip title="Voir le détail">
                                            <IconButton size="small" onClick={() => onView(product)}
                                                sx={{ color: SECTION_COLORS.rewards.main, bgcolor: `${SECTION_COLORS.rewards.main}10`, "&:hover": { bgcolor: `${SECTION_COLORS.rewards.main}20` }, width: 30, height: 30 }}>
                                                <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Modifier">
                                            <IconButton size="small" onClick={() => onEdit(product)}
                                                sx={{ color: "#6366f1", bgcolor: "#eef2ff", "&:hover": { bgcolor: "#e0e7ff" }, width: 30, height: 30 }}>
                                                <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton size="small" onClick={() => onDelete(product)}
                                                sx={{ color: "#DC2626", bgcolor: "#fef2f2", "&:hover": { bgcolor: "#fee2e2" }, width: 30, height: 30 }}>
                                                <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {filtered.length > ROWS_PER_PAGE && (
                <TablePagination
                    component="div"
                    count={filtered.length}
                    page={safePage}
                    onPageChange={onPageChange}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[ROWS_PER_PAGE]}
                    sx={{ mt: 1, borderTop: "1px solid #f0f0f0" }}
                />
            )}
        </Box>
    );
};

export default ProductsListSection;
